import { usePlayer, useGame } from "@empirica/core/player/classic/react";
import React, { useState } from "react";
import { Alert } from "../components/Alert.jsx";
import { Button } from "../components/Button.jsx";
import { savePlayerMeta } from "../utils/savePlayerMeta.js";
import { flushBehaviorTracking, useIntegrityTracking } from "../utils/integrityTracking.js";
import { getTaskConfig } from "../utils/taskConfig.js";
import { NO_SPINNER_CLASS, typedNumberInputProps } from "../utils/numberInput.js";
import { CollapsibleTaskDetails } from "../components/TaskDetailsPanel.jsx";
import {
  getNicknameFromId,
  getAnimalFromId,
  getAnimalEmoji,
} from "../components/Avatar.jsx";
import {
  getCompensationConfig,
  getPreLobbyMode,
  getReflectionTiming,
  isAsymmetricPeerAssignment,
  isBaseRateStaticSyntheticGroupCondition,
  isFourTaskPilotCondition,
  isStoryNogroupCondition,
  isStoryNogroupTemplateCondition,
  usesPostPeerExitTwoStep,
} from "../utils/conditionUtils.js";

export function ExitSurvey({ next }) {
  const labelClassName = "block text-sm font-medium text-gray-700 my-2";
  const inputClassName =
    "appearance-none block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-empirica-500 focus:border-empirica-500 sm:text-sm";
  const player = usePlayer();
  const game = useGame();

  // Get treatment information for conditional questions
  const treatment = game?.get("treatment") || {};
  const treatmentType = treatment.treatmentType;
  const isFourTaskPilot = isFourTaskPilotCondition(treatment);
  const isStoryNogroup = isStoryNogroupCondition(treatment);
  const isStoryNogroupTemplate = isStoryNogroupTemplateCondition(treatment);
  const isBaseRateStaticSyntheticGroup =
    isBaseRateStaticSyntheticGroupCondition(treatment);
  const isPostPeerReflection =
    isBaseRateStaticSyntheticGroup && getReflectionTiming(treatment) === "post-peer";
  const isIndividualPredictionTask = isStoryNogroup || isFourTaskPilot;
  const isHumanDiscussionOnly =
    !isBaseRateStaticSyntheticGroup &&
    (treatmentType === "No-AI Control" || treatmentType === "Control");
  const isEarlierParticipantReview = isBaseRateStaticSyntheticGroup;
  // The two probability-background items run in the *-assypeerassign arms only.
  // Render and validation both key on this flag, so a required item can never be
  // hidden.
  const showProbabilityBackground = isAsymmetricPeerAssignment(treatment);
  const isAIDiscussion = treatmentType === "AI-Discuss" || treatmentType === "Synthetic Peers";
  const compensation = getCompensationConfig(treatment);

  // The preparation-stage manipulation check has to name the object each arm
  // actually saw. Socratic participants converse with an AI assistant; template
  // participants complete a structured questionnaire. The construct is the same
  // in both arms, so only the referent changes, matching the wording split the
  // nogroup block already uses.
  const usesTemplatePreparation = getPreLobbyMode(treatment) === "template";
  const preparationLabel = usesTemplatePreparation
    ? "the follow-up questionnaire"
    : "the AI assistant";
  // The baseline arm runs no preparation stage, so the three items below have
  // no referent: there is no questionnaire and no assistant to rate. They are
  // dropped rather than relabelled, and export as empty strings for this arm.
  const hasPreparationStage = getPreLobbyMode(treatment) !== "none";

  // Switch-reflection ("why did you switch?") free-text. Placed here in the exit survey,
  // after the decision is final, so it cannot influence the participant's estimate. Shown in
  // every static-synthetic group condition (the *-syngroup treatments). The classification is
  // computed from stored beliefs, never shown to the participant, and never reveals correctness.
  const switchTaskConfig = getTaskConfig(treatment);
  const normalizeSwitchValue = (value) =>
    typeof switchTaskConfig.normalizePredictionValue === "function"
      ? switchTaskConfig.normalizePredictionValue(value)
      : String(value ?? "").trim();
  const switchBeliefs = game?.get("playerBeliefs") || {};
  // First value that survives normalization. `??` alone is not enough here: a
  // missing prediction normalizes to "", not to null or undefined, while a
  // genuine answer of 0 normalizes to "0" and must not be skipped.
  const firstFilledBelief = (...values) => {
    for (const value of values) {
      const normalized = normalizeSwitchValue(value);
      if (normalized !== "") return normalized;
    }
    return "";
  };
  // Anchor the branch on the answer the participant carried INTO the group stage,
  // that is, their post-AI revision. Anchoring on the pre-AI answer folds the AI
  // stage and the group stage into a single step: a participant who moved during
  // the AI conversation and then held would be told they had just changed their
  // estimate, and one who moved back to their pre-AI answer would be told they had
  // kept it. `initialBelief` is already the server's after-AI-preferring value
  // (see the playerBeliefs write in server/src/callbacks.js), so it is a safe
  // second fallback.
  const switchPreActivityBelief = firstFilledBelief(
    switchBeliefs[player.id]?.initialBeliefAfterAI,
    switchBeliefs[player.id]?.initialBelief,
    player.get("initialBeliefAfterAI"),
    player.get("initialBelief")
  );
  // Post-peer conditions elicit a second estimate right after the peer review, so
  // the branch is computed against THAT answer. Anchoring on the pre-peer answer
  // there would fold the peer review and the reflection activity into one step: a
  // participant who moved after the peers and then moved back during the activity
  // would be told they had kept their estimate. The stored payload records which
  // anchor was used, under switchTypeAnchor.
  const switchPostPeerBelief = firstFilledBelief(
    switchBeliefs[player.id]?.postPeerPrediction,
    player.get("postPeerPrediction")
  );
  const usesPostPeerAnchor = isPostPeerReflection && switchPostPeerBelief !== "";
  const switchOwnInitial = usesPostPeerAnchor
    ? switchPostPeerBelief
    : switchPreActivityBelief;
  // preLobbyPredictionBeforeAI is the client-side write, so it survives a failed
  // game-start transfer that would leave both of the first two candidates empty.
  const switchPreAiBelief = firstFilledBelief(
    switchBeliefs[player.id]?.initialBeliefBeforeAI,
    player.get("initialBeliefBeforeAI"),
    player.get("preLobbyPredictionBeforeAI")
  );
  const switchFinalBelief = firstFilledBelief(
    switchBeliefs[player.id]?.finalBelief,
    player.get("finalBelief")
  );
  const switchStaticPeersRaw = game?.get("staticSyntheticPeers") || [];
  // Keep the presentation order the participant actually saw: the server randomizes it and
  // stamps displayPosition on each peer, so sort by that and fall back to array order.
  const switchStaticPeers = (
    Array.isArray(switchStaticPeersRaw) ? switchStaticPeersRaw : []
  )
    .map((peer, index) => ({
      peer,
      displayPosition: Number(peer?.displayPosition || index + 1),
    }))
    .sort((a, b) => a.displayPosition - b.displayPosition);
  const switchPeerEstimates = switchStaticPeers.map(({ peer, displayPosition }) => ({
    estimate: normalizeSwitchValue(peer?.initialBelief),
    isCorrect: peer?.isCorrect === true,
    rationaleId: peer?.rationaleId,
    displayPosition,
  }));
  const formatSwitchBelief = (value) =>
    typeof switchTaskConfig.formatPredictionDisplay === "function"
      ? switchTaskConfig.formatPredictionDisplay(value)
      : String(value ?? "").trim();
  const switchType = (() => {
    if (!isBaseRateStaticSyntheticGroup || !switchFinalBelief) return "";
    if (switchOwnInitial && switchFinalBelief === switchOwnInitial) return "kept-own";
    const matchedPeer = switchPeerEstimates.find(
      (peer) => peer.estimate && peer.estimate === switchFinalBelief
    );
    if (matchedPeer) {
      return matchedPeer.isCorrect ? "adopted-correct-peer" : "adopted-incorrect-peer";
    }
    return "other-number";
  })();
  // Pre-peer conditions ask about the peer review alone, against the estimate the
  // participant confirmed after the preparation activity (switchOwnInitial). A
  // fixed sentence presupposed one configuration: "choosing the other
  // participant's" is false when both peers gave the same answer, and "not to
  // adopt either of their answers" is false when the participant already held a
  // peer's answer. The question therefore names the confirmed estimate, the final
  // estimate, and the two peers' answers by value, and asks only about the
  // alternatives this participant actually had. Values are interpolated, never
  // correctness, so 'adopted-correct-peer' and 'adopted-incorrect-peer' produce
  // the same sentence for the same values.
  const buildPreActivityPrompt = (type) => {
    const anchor = switchOwnInitial;
    const anchorText = formatSwitchBelief(anchor);
    const finalText = formatSwitchBelief(switchFinalBelief);
    const kept = type === "kept-own";
    // Display order, as the participant saw them.
    const peerValues = switchPeerEstimates
      .map((peer) => peer.estimate)
      .filter((value) => value !== "" && value !== undefined && value !== null);
    const hasPeers = peerValues.length >= 2;
    const peersAgree = hasPeers && peerValues[0] === peerValues[1];

    // No confirmed estimate on record: there is no baseline to name.
    if (!anchor) {
      return `Your final estimate was ${finalText}. Why did you choose ${finalText} after reading the other participants' responses?`;
    }

    const opening = kept
      ? `Your final estimate was ${finalText}, the same estimate you confirmed before reading the other participants' responses.`
      : `You changed your estimate from ${anchorText}, the estimate you confirmed before reading the other participants' responses, to ${finalText}.`;

    // Peer answers missing (seeding failure): ask without naming them.
    if (!hasPeers) {
      return kept
        ? `${opening} Why did you keep ${anchorText}?`
        : `${opening} Why did you change your estimate to ${finalText}?`;
    }

    let peerSentence;
    if (peersAgree) {
      const sharedText = formatSwitchBelief(peerValues[0]);
      if (peerValues[0] !== anchor) {
        peerSentence = `Both other participants answered ${sharedText}.`;
      } else if (kept) {
        peerSentence = `Both other participants also answered ${sharedText}.`;
      } else {
        peerSentence = `Both other participants answered ${sharedText}, the same as the estimate you confirmed.`;
      }
    } else {
      const anchorIndex = peerValues.indexOf(anchor);
      if (anchorIndex === -1) {
        peerSentence = `The other participants answered ${formatSwitchBelief(peerValues[0])} and ${formatSwitchBelief(peerValues[1])}.`;
      } else {
        const otherText = formatSwitchBelief(peerValues[1 - anchorIndex]);
        peerSentence = kept
          ? `One other participant also answered ${anchorText}, and the other answered ${otherText}.`
          : `One other participant answered ${anchorText}, the same as the estimate you confirmed, and the other answered ${otherText}.`;
      }
    }

    let question;
    if (kept) {
      const alternatives = [...new Set(peerValues.filter((value) => value !== anchor))];
      if (alternatives.length === 0) {
        question = `Why did you keep ${anchorText}?`;
      } else if (alternatives.length === 1) {
        question = `Why did you keep ${anchorText} and not switch to ${formatSwitchBelief(alternatives[0])}?`;
      } else {
        question = `Why did you keep ${anchorText} and not switch to either of their answers?`;
      }
    } else if (type === "other-number") {
      question = `Why did you choose ${finalText}?`;
    } else if (peersAgree) {
      question = "Why did you switch to their answer?";
    } else {
      const otherValue = peerValues.find((value) => value !== switchFinalBelief);
      question =
        otherValue && otherValue !== anchor
          ? `Why did you switch to ${finalText} and not keep ${anchorText} or switch to ${formatSwitchBelief(otherValue)}?`
          : `Why did you switch to ${finalText} and not keep ${anchorText}?`;
    }

    return `${opening} ${peerSentence} ${question}`;
  };
  const preActivityPrompts = {
    "adopted-correct-peer": buildPreActivityPrompt("adopted-correct-peer"),
    "adopted-incorrect-peer": buildPreActivityPrompt("adopted-incorrect-peer"),
    "kept-own": buildPreActivityPrompt("kept-own"),
    "other-number": buildPreActivityPrompt("other-number"),
  };
  // Post-peer baseline: the estimate given on the postPeerBelief stage. "kept-own"
  // here means the participant did not move during the reflection activity, which
  // says nothing about whether they had already adopted a peer's answer before it,
  // so this wording must not ask why they declined to adopt one.
  //
  // These name the two estimates by value rather than by description. Describing
  // them ("the estimate you gave after reading the other participants' responses")
  // read to participants as a claim about the whole session: someone who moved
  // 22 -> 20 after the peers and then held at 20 was told they had "kept" their
  // estimate and objected that they had in fact changed it. Both values are safe
  // to interpolate here: switchPostPeerBelief is non-empty whenever
  // usesPostPeerAnchor is true, and switchFinalBelief is non-empty whenever
  // switchType is non-empty. The 'adopted-correct-peer' and 'adopted-incorrect-peer'
  // strings are deliberately byte-identical, so correctness never leaks.
  const postPeerAnchorText = formatSwitchBelief(switchPostPeerBelief);
  const switchFinalText = formatSwitchBelief(switchFinalBelief);
  const postPeerPrompts = {
    "adopted-correct-peer": `In this last step you changed your estimate from ${postPeerAnchorText} to ${switchFinalText}, which is the same as one of the other participants' answers. Why did you decide to go with that participant's answer rather than staying at ${postPeerAnchorText} or choosing the other participant's?`,
    "adopted-incorrect-peer": `In this last step you changed your estimate from ${postPeerAnchorText} to ${switchFinalText}, which is the same as one of the other participants' answers. Why did you decide to go with that participant's answer rather than staying at ${postPeerAnchorText} or choosing the other participant's?`,
    "kept-own": `Your final estimate, ${switchFinalText}, is the same as the estimate you gave right after reading the other participants' responses. Why did you decide not to change it in this last step?`,
    "other-number": `In this last step you changed your estimate from ${postPeerAnchorText} to ${switchFinalText}, which is neither of the other participants' answers. Why did you choose ${switchFinalText}?`,
  };
  // Follow-up arms (postPeerExitTwoStep): one question about both points at
  // which the estimate can change, the peer review and the activity. The
  // estimate list above it carries the values, so the question names the two
  // steps and not the numbers. It is the same sentence for every switchType,
  // which is still computed and stored. The gate is the arm, not a filled
  // post-peer estimate: a participant whose post-peer stage timed out empty
  // still went through both steps and gets the same question, with the list
  // showing the two estimates that exist.
  const usesTwoStepSwitchReflection =
    isPostPeerReflection && usesPostPeerExitTwoStep(treatment);
  const postPeerActivityPhrase = usesTemplatePreparation
    ? "the follow-up questionnaire"
    : "your conversation with the AI assistant";
  const twoStepPrompt = `Your estimates are listed above. Please explain why you kept or changed your estimate at each of the two steps: first, after you read the other participants' responses, and second, after ${postPeerActivityPhrase}.`;
  const twoStepPrompts = {
    "adopted-correct-peer": twoStepPrompt,
    "adopted-incorrect-peer": twoStepPrompt,
    "kept-own": twoStepPrompt,
    "other-number": twoStepPrompt,
  };
  const switchReflectionPrompts = usesTwoStepSwitchReflection
    ? twoStepPrompts
    : usesPostPeerAnchor
      ? postPeerPrompts
      : preActivityPrompts;
  const showSwitchReflection = isBaseRateStaticSyntheticGroup && switchType !== "";

  // Every estimate the participant gave, in order, listed above the question so
  // the question can be about the last one without the participant having to
  // reconstruct which answer is being referred to. Only the post-peer conditions
  // get it: they are the only ones with three separate elicitations, and their
  // three values are the only ones guaranteed non-empty here. Confidence values
  // are deliberately excluded (a 0-100 confidence next to a 0-100 probability
  // estimate is exactly the confusion this block removes), and so is anything
  // about correctness or the peers' numbers, which stay behind the existing
  // collapsible below.
  const switchEstimateRows = usesPostPeerAnchor || usesTwoStepSwitchReflection
    ? [
        {
          key: "initial",
          label: "Your first estimate, before you saw the other participants' responses",
          value: switchPreAiBelief,
        },
        {
          key: "postPeer",
          label: "Your estimate right after you read the other participants' responses",
          value: switchPostPeerBelief,
        },
        { key: "final", label: "Your final estimate", value: switchFinalBelief },
      ].filter((row) => row.value !== "")
    : [];
  const showSwitchEstimateSummary =
    showSwitchReflection && switchEstimateRows.length >= 2;

  // Check if this is a lobby timeout case
  const lobbyTimeout = player.get("lobbyTimeout");
  const exitSurveyData = player.get("exitSurvey") || {};
  const isLobbyTimeout = lobbyTimeout || exitSurveyData.lobbyTimeout;

  // PRIMARY CHECK: Did player successfully enter the group?
  // Player entered group if they were in playersSnapshot when game started
  const gameStarted = game?.get("startedAt");
  const playersSnapshot = game?.get("playersSnapshot") || [];
  const playerInGroup = playersSnapshot.some(p => p.id === player.id);
  const enteredGroup = !!(gameStarted && playerInGroup);

  // FALLBACK: Use finalBelief logic if group entry check is inconclusive
  // Check player object directly for finalBelief (stored by callbacks.js)
  const playerFinalBelief = player.get("finalBelief");

  // Check game.playerBeliefs object as backup
  const playerBeliefs = game?.get("playerBeliefs") || {};
  const finalBeliefInGame = playerBeliefs[player.id]?.finalBelief;

  // Check current round as fallback (may be null when exit survey shows)
  const currentRound = player.round;
  const finalBeliefInRound = currentRound?.get?.("finalBelief");

  // Player has finalBelief if ANY of these sources have it
  const hasFinalBelief = !!(playerFinalBelief || finalBeliefInGame || finalBeliefInRound);
  const completedSoloTask = isFourTaskPilot && !!player.get("fourTaskCompleted");

  // Player had successful group experience if they entered group OR have finalBelief data
  const hadSuccessfulExperience = completedSoloTask || enteredGroup || hasFinalBelief;

  // Log for debugging
  console.info("[EXIT SURVEY] Group entry check:", {
    playerId: player.id,
    gameStarted,
    playersSnapshotCount: playersSnapshot.length,
    playerInGroup,
    enteredGroup,
    hasFinalBelief,
    hadSuccessfulExperience,
    isLobbyTimeout
  });

  // Lobby failure = neither entered group nor has finalBelief (regardless of lobby timeout status)
  const isLobbyFailure = !hadSuccessfulExperience;

  const [currentPage, setCurrentPage] = useState(
    isLobbyFailure ? 3 : 1
  ); // Start at page 3 (thank you) for lobby failure cases (didn't enter group)

  // Page 1 responses
  const [switchReflectionText, setSwitchReflectionText] = useState(
    () => player.get("switchReflection")?.response || ""
  );

  // The exit survey is an Empirica exitStep, rendered instead of <Game/>, so
  // the Game-level integrity hook never covered it. `switchReflection` is
  // analysed free text and the outside-tool text blob scans it, but until now a
  // paste into it carried no provenance record at all. Mounting the hook here
  // closes the last free-text field in the study that was outside the tracker.
  useIntegrityTracking({
    enabled: Boolean(player?.id),
    player,
    phase: "exit",
    defaultStageId: "exit-survey",
    defaultStageName: "exit-survey",
  });
  // Collapsed by default: the participant may reopen the two responses while answering.
  const [showSwitchPeerRationales, setShowSwitchPeerRationales] = useState(false);
  const [switchPeerReviewOpens, setSwitchPeerReviewOpens] = useState(0);
  // Retained for the live-discussion and nogroup conditions only. The
  // static-synthetic group conditions no longer show these four: they load on a
  // single factor with informationInformative (Cronbach alpha 0.848 on the
  // pooled N=832), so they were measuring one construct four times.
  const [groupSatisfaction, setGroupSatisfaction] = useState("");
  const [conformityPressure, setConformityPressure] = useState("");
  const [clearSenseOfOthers, setClearSenseOfOthers] = useState("");
  const [contentEngagement, setContentEngagement] = useState("");
  const [informationInformative, setInformationInformative] = useState("");
  const [mentalEffortRequired, setMentalEffortRequired] = useState("");
  // Preparation-stage manipulation check. The coaching manipulation is the
  // treatment the study is about, and until now nothing asked whether the two
  // arms were experienced differently. The third item is a participant-side
  // fidelity check on the instruction that the assistant never reveals or
  // corrects the answer, complementing the transcript audit.
  const [prepTailoredQuestions, setPrepTailoredQuestions] = useState("");
  const [prepConsideredCounterReasons, setPrepConsideredCounterReasons] = useState("");
  const [prepRevealedAnswer, setPrepRevealedAnswer] = useState("");
  // Informational and normative influence, separated. The single
  // conformityPressure item they replace rose with the randomized number of
  // correct peers, so it was measuring perceived informational pull under a
  // normative name, which is the one claim the design is meant to isolate.
  const [peerInfluenceInformational, setPeerInfluenceInformational] = useState("");
  const [peerInfluenceNormative, setPeerInfluenceNormative] = useState("");
  // The informational-influence interpretation rests on participants believing
  // the two responses came from earlier people, which the instructions state
  // openly. This records whether the belief held.
  const [peersWrittenByParticipants, setPeersWrittenByParticipants] = useState("");
  // Mechanism items added in October 2026, during data collection: whether the
  // preparation activity made the participant consider other answers
  // (receptivity), helped them reason about the problem (learning), or helped
  // them judge the two responses, and an open account of what it did for them.
  // They follow every existing item, which keeps the position and the preceding
  // questions of each existing item the same for all participants.
  const [prepConsideredOtherAnswers, setPrepConsideredOtherAnswers] = useState("");
  const [prepReasonedBetter, setPrepReasonedBetter] = useState("");
  const [prepJudgedResponses, setPrepJudgedResponses] = useState("");
  const [prepOpenFeedback, setPrepOpenFeedback] = useState("");
  const [aiTrust, setAiTrust] = useState("");
  const [aiUsage, setAiUsage] = useState("");
  const [usedOutsideHelp, setUsedOutsideHelp] = useState("");
  const [probabilityCourse, setProbabilityCourse] = useState("");
  const [priorBayesRule, setPriorBayesRule] = useState("");
  const [age, setAge] = useState("");
  const [gender, setGender] = useState("");
  const [education, setEducation] = useState("");

  // Page 2 responses
  const [gameClarity, setGameClarity] = useState("");
  const [fairPay, setFairPay] = useState("");
  const [displayProblems, setDisplayProblems] = useState("");
  const [additionalFeedback, setAdditionalFeedback] = useState("");

  function handleSubmit(event) {
    event.preventDefault();
    // Many participants close the tab on the completion-code page instead of
    // clicking through, so write the behavior log at every exit-survey submit.
    flushBehaviorTracking();

    // For lobby failure cases (didn't enter group), skip to thank you page
    if (isLobbyFailure) {
      // Ensure exit survey data is submitted for failure cases
      if (!player.get("exitSurvey")) {
        const failurePayload = {
          lobbyTimeout: isLobbyTimeout,
          lobbyFailure: true,
          failureAt: Date.now(),
          completed: false,
          paymentType: "base",
          reason: "lobby_failure"
        };

        console.info("[CLIENT EXIT SURVEY] Setting lobby failure/timeout data for player", player.id, failurePayload);
        player.set("exitSurvey", failurePayload);
        player.set("readyToExit", true);

        // Also trigger game-level event for server processing
        if (game) {
          console.info("[CLIENT EXIT SURVEY] Triggering game-level event for lobby failure/timeout, game:", game.id, "player:", player.id);
          const eventData = { playerId: player.id, data: failurePayload };
          console.info("[CLIENT EXIT SURVEY] Lobby failure/timeout event data:", eventData);
          game.set("playerExitSurveySubmitted", eventData);
          console.info("[CLIENT EXIT SURVEY] Game.set() for lobby failure/timeout called successfully");
        } else {
          console.warn("[CLIENT EXIT SURVEY] No game found for lobby failure/timeout player", player.id);
        }
      }
      setCurrentPage(3);
      return;
    }

    if (currentPage === 1) {
      if (!validatePage1()) {
        alert("Please answer all questions before proceeding.");
        return;
      }
      if (showSwitchReflection) {
        const switchReflectionPayload = {
          switchType,
          // Which baseline switchType was computed against. The 2026-07-25 wave
          // used the pre-AI answer; pre-peer waves from here on use the post-AI
          // one; post-peer conditions use the estimate given right after the peer
          // review, so the branch isolates change during the reflection activity.
          // Analysis code can tell the three vintages apart without guessing.
          switchTypeAnchor: usesPostPeerAnchor ? "post-peer" : "post-ai",
          initialBelief: switchOwnInitial,
          initialBeliefBeforeAI: switchPreAiBelief,
          // Always recorded for post-peer conditions so the full trajectory
          // (pre-peer -> post-peer -> final) is reconstructable from this payload.
          preActivityBelief: switchPreActivityBelief,
          postPeerBelief: switchPostPeerBelief,
          finalBelief: switchFinalBelief,
          peers: switchPeerEstimates,
          response: switchReflectionText.trim(),
          // The exact sentence this participant answered, and whether the
          // estimate-trajectory summary was on screen above it. Both prompts now
          // interpolate values, so the wording is no longer recoverable from
          // switchType alone.
          promptText: switchReflectionPrompts[switchType],
          ...(usesTwoStepSwitchReflection ? { promptMode: "two-step" } : {}),
          estimateSummaryShown: showSwitchEstimateSummary,
          // Whether the participant reopened the two responses while answering.
          reviewedPeerRationales: switchPeerReviewOpens > 0,
          peerRationaleReviewOpens: switchPeerReviewOpens,
          timestamp: Date.now(),
        };
        player.set("switchReflection", switchReflectionPayload);
        savePlayerMeta({
          player,
          name: "switchReflection",
          value: switchReflectionPayload,
          debugTag: "exit survey: switch reflection",
        });
      }
      setCurrentPage(2);
    } else if (currentPage === 2) {
      const payload = {
        // Page 1, live-discussion and nogroup conditions only
        groupSatisfaction,
        conformityPressure,
        clearSenseOfOthers,
        contentEngagement,
        // Page 1, all conditions
        informationInformative,
        mentalEffortRequired,
        // Preparation-stage manipulation check and fidelity check
        prepTailoredQuestions,
        prepConsideredCounterReasons,
        prepRevealedAnswer,
        // Influence channels, replacing conformityPressure in the
        // static-synthetic group conditions
        peerInfluenceInformational,
        peerInfluenceNormative,
        peersWrittenByParticipants,
        // Mechanism items added in October 2026; empty in arms without a
        // preparation stage
        prepConsideredOtherAnswers,
        prepReasonedBetter,
        prepJudgedResponses,
        prepOpenFeedback,
        aiTrust,
        aiUsage,
        usedOutsideHelp,
        // Probability background, *-assypeerassign arms only
        probabilityCourse,
        priorBayesRule,
        age,
        gender,
        education,
        // Page 2
        gameClarity,
        fairPay,
        displayProblems,
        additionalFeedback,
      };
      console.info("[CLIENT EXIT SURVEY] About to call player.set for player", player.id);
      console.info("[CLIENT EXIT SURVEY] Payload:", JSON.stringify(payload, null, 2));
      console.info("[CLIENT EXIT SURVEY] Player object details:", { id: player.id, hasSet: typeof player.set === 'function' });

      // Set the exit survey data
      player.set("exitSurvey", payload);
      savePlayerMeta({ player, name: "exitSurvey", value: payload, debugTag: "Exit Survey: all cases" });

      console.info("[CLIENT EXIT SURVEY] player.set() called successfully for player", player.id);
      console.info("[CLIENT EXIT SURVEY] Exit survey data after set:", player.get("exitSurvey"));
      console.info("[CLIENT EXIT SURVEY] Player submitted:", player.id, payload);

      // Also trigger a game-level event for immediate server processing
      if (game) {
        console.info("[CLIENT EXIT SURVEY] Triggering game-level event for exit survey, game:", game.id, "player:", player.id);
        const eventData = { playerId: player.id, data: payload };
        console.info("[CLIENT EXIT SURVEY] Event data:", eventData);
        game.set("playerExitSurveySubmitted", eventData);
        console.info("[CLIENT EXIT SURVEY] Game.set() called successfully");
      } else {
        console.warn("[CLIENT EXIT SURVEY] No game found for player", player.id);
      }

      setCurrentPage(3);
    } else {
      // On final page, mark player as ready to exit and complete the exit survey
      console.info("[CLIENT EXIT SURVEY] Setting readyToExit and calling next() for player", player.id);
      player.set("readyToExit", true);
      next();
    }
  }

  function handleEducationChange(e) {
    setEducation(e.target.value);
  }

  // Validation function for page 1
  function validatePage1() {
    const requiredFields = [aiTrust, aiUsage, age, gender, education];

    if (showSwitchReflection) {
      requiredFields.push(switchReflectionText.trim());
    }

    // The four-item perception block and the conformity item stay required in
    // the live-discussion conditions, which still render them.
    if (!isIndividualPredictionTask && !isEarlierParticipantReview) {
      requiredFields.push(groupSatisfaction, conformityPressure);
    }

    if (isHumanDiscussionOnly || isAIDiscussion) {
      requiredFields.push(
        clearSenseOfOthers,
        informationInformative,
        mentalEffortRequired,
        contentEngagement
      );
    }

    if (isEarlierParticipantReview) {
      // The three preparation-stage items render only behind hasPreparationStage,
      // so requiring them unconditionally left the baseline arm
      // (preLobbyMode: none) with three required fields that never appear on
      // screen. Page 1 could then never validate: the participant answered every
      // visible question and still got "Please answer all questions before
      // proceeding" on every attempt, with no way to reach the end of the study.
      if (hasPreparationStage) {
        requiredFields.push(
          prepTailoredQuestions,
          prepConsideredCounterReasons,
          prepRevealedAnswer
        );
      }
      requiredFields.push(
        informationInformative,
        mentalEffortRequired,
        peerInfluenceInformational,
        peerInfluenceNormative,
        peersWrittenByParticipants
      );
      // The four mechanism items render behind the same hasPreparationStage
      // guard, so the baseline arm is never asked for fields it cannot see.
      if (hasPreparationStage) {
        requiredFields.push(
          prepConsideredOtherAnswers,
          prepReasonedBetter,
          prepJudgedResponses,
          prepOpenFeedback.trim()
        );
      }
    }

    if (isStoryNogroup || isFourTaskPilot) {
      requiredFields.push(
        informationInformative,
        mentalEffortRequired,
        contentEngagement
      );
    }

    // Self-report on outside help now runs in the static-synthetic group
    // conditions too, so the paste and trap-phrase flags have something to be
    // triangulated against.
    if (isFourTaskPilot || isEarlierParticipantReview) {
      requiredFields.push(usedOutsideHelp);
    }

    if (showProbabilityBackground) {
      requiredFields.push(probabilityCourse, priorBayesRule);
    }

    return requiredFields.every(field => field !== "" && field !== null && field !== undefined);
  }

  function renderPage1() {
    return (
      <div className="space-y-8 mt-6">
        <div>
          <h3 className="text-lg leading-6 font-medium text-gray-900 mb-4">
            Survey - Page 1 of 3
          </h3>
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4">
            <p className="text-sm text-blue-800 mb-2">
              <strong>Note:</strong> Please finish the survey and you will find completion code at the end.
            </p>
          </div>
        </div>

        {/* The switch-reflection question below asks about the estimate, so the
            task stays one click away here as it does on every screen after the
            first prediction page. */}
        {isEarlierParticipantReview && (
          <CollapsibleTaskDetails
            taskConfig={switchTaskConfig}
            scrollable
            className="rounded-md border border-gray-200 bg-white p-4"
          />
        )}

        {/* Switch-reflection free-text - static-synthetic group (syngroup) conditions only */}
        {showSwitchReflection && (
          <div>
            {showSwitchEstimateSummary && (
              <div className="mb-3 rounded-md border border-gray-200 bg-gray-50 p-4">
                <div className="text-sm font-semibold text-gray-900 mb-2">
                  The estimates you gave, in order
                </div>
                <ol className="space-y-1">
                  {switchEstimateRows.map((row, index) => (
                    <li
                      key={row.key}
                      className="flex items-baseline justify-between gap-4 text-sm"
                    >
                      <span
                        className={
                          row.key === "final"
                            ? "font-medium text-gray-900"
                            : "text-gray-700"
                        }
                      >
                        {index + 1}. {row.label}
                      </span>
                      <span
                        className={
                          row.key === "final"
                            ? "font-semibold text-gray-900"
                            : "font-medium text-gray-800"
                        }
                      >
                        {formatSwitchBelief(row.value)}
                      </span>
                    </li>
                  ))}
                </ol>
                <p className="mt-2 text-xs text-gray-600">
                  {usesTwoStepSwitchReflection
                    ? "The question below is about both steps."
                    : "The question below is about the last one."}
                </p>
              </div>
            )}

            <label className={labelClassName}>
              {switchReflectionPrompts[switchType]}
            </label>

            {/* Optional re-read of the two earlier participant responses, collapsed by
                default so it does not prime the answer. Shown in the same order the
                participant saw them during the review phase. */}
            {switchStaticPeers.length > 0 && (
              <div className="mb-3 rounded-md border border-gray-200 bg-gray-50 p-3">
                <button
                  type="button"
                  className="text-sm font-medium text-empirica-600 underline hover:text-empirica-700 focus:outline-none focus:ring-2 focus:ring-empirica-300 rounded"
                  aria-expanded={showSwitchPeerRationales}
                  onClick={() => {
                    setShowSwitchPeerRationales((prev) => {
                      if (!prev) {
                        setSwitchPeerReviewOpens((count) => count + 1);
                      }
                      return !prev;
                    });
                  }}
                >
                  {showSwitchPeerRationales
                    ? "Hide the other participants' responses"
                    : "Click here to review the other participants' responses again"}
                </button>

                {showSwitchPeerRationales && (
                  <div className="mt-3 space-y-3">
                    {switchStaticPeers.map(({ peer, displayPosition }) => {
                      const peerId = peer?.id;
                      const nickname = peer?.name || getNicknameFromId(peerId);
                      const animal =
                        peer?.assignedAnimal || getAnimalFromId(peerId);
                      const emoji = getAnimalEmoji(animal);
                      const estimate = peer?.initialBelief;
                      const hasEstimate =
                        estimate !== undefined &&
                        estimate !== null &&
                        String(estimate).trim() !== "";
                      const rationaleText =
                        String(peer?.rationaleText || "").trim() ||
                        "This earlier participant response is unavailable.";

                      return (
                        <div
                          key={peerId || displayPosition}
                          className="rounded-lg border border-gray-200 bg-white p-4"
                        >
                          <div className="mb-2 flex items-center gap-3">
                            <div className="h-10 w-10 rounded-full bg-gray-50 shadow flex items-center justify-center text-xl">
                              <span aria-hidden>{emoji}</span>
                            </div>
                            <div>
                              <div className="font-semibold text-gray-900">
                                {nickname}
                              </div>
                              {hasEstimate && (
                                <div className="text-sm text-gray-600">
                                  Estimate:{" "}
                                  <span className="font-medium">
                                    {formatSwitchBelief(estimate)}
                                  </span>
                                </div>
                              )}
                            </div>
                          </div>
                          <p className="whitespace-pre-wrap text-sm leading-6 text-gray-800">
                            {rationaleText}
                          </p>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            )}

            <textarea
              className={inputClassName}
              rows={4}
              placeholder="Please explain in a sentence or two."
              value={switchReflectionText}
              onChange={(e) => setSwitchReflectionText(e.target.value)}
            />
          </div>
        )}

        {/* Group Results Satisfaction - live-discussion conditions only */}
        {!isIndividualPredictionTask && !isEarlierParticipantReview && (
          <div>
            <label className={labelClassName}>
              How satisfied are you with the group results?
            </label>
            <LikertScale7
              name="groupSatisfaction"
              value={groupSatisfaction}
              onChange={setGroupSatisfaction}
              labels={["Very Dissatisfied", "", "", "Neutral", "", "", "Very Satisfied"]}
            />
          </div>
        )}

        {/* Conformity Pressure - live-discussion conditions only. The
            static-synthetic group conditions ask the two influence-channel
            items below instead, which separate the informational pull this
            single item was actually picking up from the normative pressure its
            wording names. */}
        {!isIndividualPredictionTask && !isEarlierParticipantReview && (
          <div>
            <label className={labelClassName}>
              I felt pressure to conform because of others' opinions.
            </label>
            <LikertScale7
              name="conformityPressure"
              value={conformityPressure}
              onChange={setConformityPressure}
              labels={["Strongly Disagree", "", "", "Neutral", "", "", "Strongly Agree"]}
            />
          </div>
        )}

        {/* Static-synthetic group conditions. The order follows the order the
            participant experienced the session: the preparation stage first,
            then the peer review, then the two influence channels. */}
        {isEarlierParticipantReview && (
          <>
            {hasPreparationStage && (
              <>
            <div>
              <label className={labelClassName}>
                {`In the follow-up step after ${
                  usesPostPeerExitTwoStep(treatment)
                    ? "I reviewed the other participants' responses"
                    : "my first estimate"
                }, ${preparationLabel} asked questions that were tailored to what I had written.`}
              </label>
              <LikertScale7
                name="prepTailoredQuestions"
                value={prepTailoredQuestions}
                onChange={setPrepTailoredQuestions}
                labels={["Strongly Disagree", "", "", "Neutral", "", "", "Strongly Agree"]}
              />
            </div>

            <div>
              <label className={labelClassName}>
                {`${preparationLabel.charAt(0).toUpperCase()}${preparationLabel.slice(1)} made me consider reasons my answer might be wrong.`}
              </label>
              <LikertScale7
                name="prepConsideredCounterReasons"
                value={prepConsideredCounterReasons}
                onChange={setPrepConsideredCounterReasons}
                labels={["Strongly Disagree", "", "", "Neutral", "", "", "Strongly Agree"]}
              />
            </div>

            <div>
              <label className={labelClassName}>
                {`${preparationLabel.charAt(0).toUpperCase()}${preparationLabel.slice(1)} told me something about what the correct answer was.`}
              </label>
              <LikertScale7
                name="prepRevealedAnswer"
                value={prepRevealedAnswer}
                onChange={setPrepRevealedAnswer}
                labels={["Strongly Disagree", "", "", "Neutral", "", "", "Strongly Agree"]}
              />
            </div>
              </>
            )}

            <div>
              <label className={labelClassName}>
                The information I received during the peer-response review phase was informative.
              </label>
              <LikertScale7
                name="informationInformative"
                value={informationInformative}
                onChange={setInformationInformative}
                labels={["Strongly Disagree", "", "", "Neutral", "", "", "Strongly Agree"]}
              />
            </div>

            <div>
              <label className={labelClassName}>
                Following the information presented in the peer-response review phase required a lot of mental effort.
              </label>
              <LikertScale7
                name="mentalEffortRequired"
                value={mentalEffortRequired}
                onChange={setMentalEffortRequired}
                labels={["Strongly Disagree", "", "", "Neutral", "", "", "Strongly Agree"]}
              />
            </div>

            <div>
              <label className={labelClassName}>
                I felt pulled toward the other participants' answers because their reasoning made sense to me.
              </label>
              <LikertScale7
                name="peerInfluenceInformational"
                value={peerInfluenceInformational}
                onChange={setPeerInfluenceInformational}
                labels={["Strongly Disagree", "", "", "Neutral", "", "", "Strongly Agree"]}
              />
            </div>

            <div>
              <label className={labelClassName}>
                I felt pulled toward the other participants' answers because I did not want to be the only one with a different answer.
              </label>
              <LikertScale7
                name="peerInfluenceNormative"
                value={peerInfluenceNormative}
                onChange={setPeerInfluenceNormative}
                labels={["Strongly Disagree", "", "", "Neutral", "", "", "Strongly Agree"]}
              />
            </div>

            <div>
              <label className={labelClassName}>
                The two responses I reviewed were written by earlier human participants.
              </label>
              <LikertScale7
                name="peersWrittenByParticipants"
                value={peersWrittenByParticipants}
                onChange={setPeersWrittenByParticipants}
                labels={["Strongly Disagree", "", "", "Neutral", "", "", "Strongly Agree"]}
              />
            </div>

            {hasPreparationStage && (
              <>
            <div>
              <label className={labelClassName}>
                {`${preparationLabel.charAt(0).toUpperCase()}${preparationLabel.slice(1)} made me consider answers other than my own.`}
              </label>
              <LikertScale7
                name="prepConsideredOtherAnswers"
                value={prepConsideredOtherAnswers}
                onChange={setPrepConsideredOtherAnswers}
                labels={["Strongly Disagree", "", "", "Neutral", "", "", "Strongly Agree"]}
              />
            </div>

            <div>
              <label className={labelClassName}>
                {`${preparationLabel.charAt(0).toUpperCase()}${preparationLabel.slice(1)} helped me reason better about the problem.`}
              </label>
              <LikertScale7
                name="prepReasonedBetter"
                value={prepReasonedBetter}
                onChange={setPrepReasonedBetter}
                labels={["Strongly Disagree", "", "", "Neutral", "", "", "Strongly Agree"]}
              />
            </div>

            <div>
              <label className={labelClassName}>
                {`${preparationLabel.charAt(0).toUpperCase()}${preparationLabel.slice(1)} helped me judge which of the two responses I reviewed had better reasoning.`}
              </label>
              <LikertScale7
                name="prepJudgedResponses"
                value={prepJudgedResponses}
                onChange={setPrepJudgedResponses}
                labels={["Strongly Disagree", "", "", "Neutral", "", "", "Strongly Agree"]}
              />
            </div>

            <div>
              <label className={labelClassName}>
                {`What did you think of ${preparationLabel}? Did it help you, and did it change your answer or how confident you were in it? Please explain how.`}
              </label>
              <textarea
                className={inputClassName}
                rows={4}
                name="prepOpenFeedback"
                placeholder="Please explain in a sentence or two."
                value={prepOpenFeedback}
                onChange={(e) => setPrepOpenFeedback(e.target.value)}
              />
            </div>
              </>
            )}

            <div>
              <label className={labelClassName}>
                While working on this task, did you use any outside help such as
                AI systems, LLMs, or another person?
              </label>
              <p className="text-sm text-gray-600 mb-2">
                Your answer will not affect your payment or your bonus, both of
                which are already determined. Please answer honestly.
              </p>
              <div className="mt-2 flex gap-6">
                <Radio
                  selected={usedOutsideHelp}
                  name="usedOutsideHelp"
                  value="yes"
                  label="Yes"
                  onChange={(event) => setUsedOutsideHelp(event.target.value)}
                />
                <Radio
                  selected={usedOutsideHelp}
                  name="usedOutsideHelp"
                  value="no"
                  label="No"
                  onChange={(event) => setUsedOutsideHelp(event.target.value)}
                />
              </div>
            </div>
          </>
        )}

        {isHumanDiscussionOnly && (
          <>
            {/* Clear Sense of Others */}
            <div>
              <label className={labelClassName}>
                I have a clear sense of what most other participants think about this issue.
              </label>
              <LikertScale7
                name="clearSenseOfOthers"
                value={clearSenseOfOthers}
                onChange={setClearSenseOfOthers}
                labels={["Strongly Disagree", "", "", "Neutral", "", "", "Strongly Agree"]}
              />
            </div>

            {/* Information Informative - Human Discussion */}
            <div>
              <label className={labelClassName}>
                The information I received during group discussion phase was informative.
              </label>
              <LikertScale7
                name="informationInformative"
                value={informationInformative}
                onChange={setInformationInformative}
                labels={["Strongly Disagree", "", "", "Neutral", "", "", "Strongly Agree"]}
              />
            </div>

            {/* Mental Effort Required - Human Discussion */}
            <div>
              <label className={labelClassName}>
                Following the information presented in group discussion phase required a lot of mental effort.
              </label>
              <LikertScale7
                name="mentalEffortRequired"
                value={mentalEffortRequired}
                onChange={setMentalEffortRequired}
                labels={["Strongly Disagree", "", "", "Neutral", "", "", "Strongly Agree"]}
              />
            </div>

            {/* Content Engagement - Human Discussion */}
            <div>
              <label className={labelClassName}>
                I felt engaged with the content during group discussion phase.
              </label>
              <LikertScale7
                name="contentEngagement"
                value={contentEngagement}
                onChange={setContentEngagement}
                labels={["Strongly Disagree", "", "", "Neutral", "", "", "Strongly Agree"]}
              />
            </div>
          </>
        )}

        {isAIDiscussion && (
          <>
            {/* Clear Sense of Others */}
            <div>
              <label className={labelClassName}>
                I have a clear sense of what most other participants think about this issue.
              </label>
              <LikertScale7
                name="clearSenseOfOthers"
                value={clearSenseOfOthers}
                onChange={setClearSenseOfOthers}
                labels={["Strongly Disagree", "", "", "Neutral", "", "", "Strongly Agree"]}
              />
            </div>

            {/* Information Informative - AI Discussion */}
            <div>
              <label className={labelClassName}>
                The information I received during AI discussion and summary review phase was informative.
              </label>
              <LikertScale7
                name="informationInformative"
                value={informationInformative}
                onChange={setInformationInformative}
                labels={["Strongly Disagree", "", "", "Neutral", "", "", "Strongly Agree"]}
              />
            </div>

            {/* Mental Effort Required - AI Discussion */}
            <div>
              <label className={labelClassName}>
                Following the information presented in AI discussion and summary review phase required a lot of mental effort.
              </label>
              <LikertScale7
                name="mentalEffortRequired"
                value={mentalEffortRequired}
                onChange={setMentalEffortRequired}
                labels={["Strongly Disagree", "", "", "Neutral", "", "", "Strongly Agree"]}
              />
            </div>

            {/* Content Engagement - AI Discussion */}
            <div>
              <label className={labelClassName}>
                I felt engaged with the content during AI discussion and summary review phase.
              </label>
              <LikertScale7
                name="contentEngagement"
                value={contentEngagement}
                onChange={setContentEngagement}
                labels={["Strongly Disagree", "", "", "Neutral", "", "", "Strongly Agree"]}
              />
            </div>
          </>
        )}

        {/* Nogroup-specific AI assistant questions */}
        {isStoryNogroup && (
          <>
            {/* Information Informative - AI Assistant */}
            <div>
              <label className={labelClassName}>
                {isStoryNogroupTemplate
                  ? "The follow-up questionnaire helped me think through my prediction."
                  : "The follow-up questions from the AI assistant helped me think through my prediction."}
              </label>
              <LikertScale7
                name="informationInformative"
                value={informationInformative}
                onChange={setInformationInformative}
                labels={["Strongly Disagree", "", "", "Neutral", "", "", "Strongly Agree"]}
              />
            </div>

            {/* Mental Effort Required - AI Assistant */}
            <div>
              <label className={labelClassName}>
                {isStoryNogroupTemplate
                  ? "Articulating my reasoning in the follow-up questionnaire required a lot of mental effort."
                  : "Articulating my reasoning to the AI assistant required a lot of mental effort."}
              </label>
              <LikertScale7
                name="mentalEffortRequired"
                value={mentalEffortRequired}
                onChange={setMentalEffortRequired}
                labels={["Strongly Disagree", "", "", "Neutral", "", "", "Strongly Agree"]}
              />
            </div>

            {/* Content Engagement - AI Assistant */}
            <div>
              <label className={labelClassName}>
                {isStoryNogroupTemplate
                  ? "I felt engaged while completing the follow-up questionnaire."
                  : "I felt engaged during the conversation with the AI assistant."}
              </label>
              <LikertScale7
                name="contentEngagement"
                value={contentEngagement}
                onChange={setContentEngagement}
                labels={["Strongly Disagree", "", "", "Neutral", "", "", "Strongly Agree"]}
              />
            </div>
          </>
        )}

        {isFourTaskPilot && (
          <>
            <div>
              <label className={labelClassName}>
                The instructions and question wording made it clear what I was
                supposed to do.
              </label>
              <LikertScale7
                name="informationInformative"
                value={informationInformative}
                onChange={setInformationInformative}
                labels={["Strongly Disagree", "", "", "Neutral", "", "", "Strongly Agree"]}
              />
            </div>

            <div>
              <label className={labelClassName}>
                Answering the four questions required a lot of mental effort.
              </label>
              <LikertScale7
                name="mentalEffortRequired"
                value={mentalEffortRequired}
                onChange={setMentalEffortRequired}
                labels={["Strongly Disagree", "", "", "Neutral", "", "", "Strongly Agree"]}
              />
            </div>

            <div>
              <label className={labelClassName}>
                I felt engaged while answering the four questions.
              </label>
              <LikertScale7
                name="contentEngagement"
                value={contentEngagement}
                onChange={setContentEngagement}
                labels={["Strongly Disagree", "", "", "Neutral", "", "", "Strongly Agree"]}
              />
            </div>

            <div>
              <label className={labelClassName}>
                While answering the four questions, did you use any outside help
                such as AI systems, LLMs, or another person?
              </label>
              <div className="mt-2 flex gap-6">
                <Radio
                  selected={usedOutsideHelp}
                  name="usedOutsideHelp"
                  value="yes"
                  label="Yes"
                  onChange={(event) => setUsedOutsideHelp(event.target.value)}
                />
                <Radio
                  selected={usedOutsideHelp}
                  name="usedOutsideHelp"
                  value="no"
                  label="No"
                  onChange={(event) => setUsedOutsideHelp(event.target.value)}
                />
              </div>
            </div>
          </>
        )}

        {/* AI Trust */}
        <div>
          <label className={labelClassName}>
            {isStoryNogroupTemplate
              ? "How much do you trust automated decision-support tools in general?"
              : "How much do you trust AI in general?"}
          </label>
          <LikertScale7
            name="aiTrust"
            value={aiTrust}
            onChange={setAiTrust}
            labels={["No Trust", "", "", "Neutral", "", "", "Complete Trust"]}
          />
        </div>

        {/* AI Usage in Daily Life */}
        <div>
          <label className={labelClassName}>
            {isStoryNogroupTemplate
              ? "How often do you use automated decision-support tools in your daily life?"
              : "How often do you use AI in your daily life?"}
          </label>
          <LikertScale7
            name="aiUsage"
            value={aiUsage}
            onChange={setAiUsage}
            labels={["Never", "Very Rarely", "Rarely", "Sometimes", "Often", "Very Often", "Daily"]}
          />
        </div>

        {/* Probability background, *-assypeerassign arms only */}
        {showProbabilityBackground && (
          <>
            <div>
              <label className={labelClassName}>
                Have you completed a course in probability or statistics at the
                college level or above?
              </label>
              <div className="mt-2 flex gap-6">
                <Radio
                  selected={probabilityCourse}
                  name="probabilityCourse"
                  value="yes"
                  label="Yes"
                  onChange={(event) => setProbabilityCourse(event.target.value)}
                />
                <Radio
                  selected={probabilityCourse}
                  name="probabilityCourse"
                  value="no"
                  label="No"
                  onChange={(event) => setProbabilityCourse(event.target.value)}
                />
              </div>
            </div>

            <div>
              <label className={labelClassName}>
                Before this study, had you learned Bayes' rule (also called
                Bayes' theorem)?
              </label>
              <div className="mt-2 flex gap-6">
                <Radio
                  selected={priorBayesRule}
                  name="priorBayesRule"
                  value="yes"
                  label="Yes"
                  onChange={(event) => setPriorBayesRule(event.target.value)}
                />
                <Radio
                  selected={priorBayesRule}
                  name="priorBayesRule"
                  value="no"
                  label="No"
                  onChange={(event) => setPriorBayesRule(event.target.value)}
                />
              </div>
            </div>
          </>
        )}

        {/* Demographics */}
        <div className="flex flex-row gap-6">
          <div className="flex-1">
            <label htmlFor="age" className={labelClassName}>
              Age
            </label>
            <div className="mt-1">
              <input
                id="age"
                name="age"
                type="number"
                min="18"
                max="100"
                autoComplete="off"
                className={`${inputClassName} ${NO_SPINNER_CLASS}`}
                value={age}
                onChange={(e) => setAge(e.target.value)}
                {...typedNumberInputProps()}
              />
            </div>
          </div>
          <div className="flex-1">
            <label htmlFor="gender" className={labelClassName}>
              Gender
            </label>
            <div className="mt-1">
              <select
                id="gender"
                name="gender"
                className={inputClassName}
                value={gender}
                onChange={(e) => setGender(e.target.value)}
              >
                <option value="">Select...</option>
                <option value="male">Male</option>
                <option value="female">Female</option>
                <option value="non-binary">Non-binary</option>
                <option value="prefer-not-to-say">Prefer not to say</option>
                <option value="other">Other</option>
              </select>
            </div>
          </div>
        </div>

        {/* Education */}
        <div>
          <label className={labelClassName}>
            What is your highest level of education?
          </label>
          <div className="grid gap-2">
            <Radio
              selected={education}
              name="education"
              value="high-school"
              label="High School or equivalent"
              onChange={handleEducationChange}
            />
            <Radio
              selected={education}
              name="education"
              value="some-college"
              label="Some College"
              onChange={handleEducationChange}
            />
            <Radio
              selected={education}
              name="education"
              value="bachelor"
              label="Bachelor's Degree"
              onChange={handleEducationChange}
            />
            <Radio
              selected={education}
              name="education"
              value="master"
              label="Master's Degree or higher"
              onChange={handleEducationChange}
            />
            <Radio
              selected={education}
              name="education"
              value="other"
              label="Other"
              onChange={handleEducationChange}
            />
          </div>
        </div>
      </div>
    );
  }

  function renderPage2() {
    return (
      <div className="space-y-8 mt-6">
        <div>
          <h3 className="text-lg leading-6 font-medium text-gray-900 mb-4">
            Survey - Page 2 of 3
          </h3>
          <p className="text-sm text-gray-600 mb-6">
            {isIndividualPredictionTask
              ? "These responses will not affect your payment. We want to understand your experience."
              : "These responses will not affect your payment. We want to understand your game experience."}
          </p>
        </div>

        {/* Game Clarity */}
        <div>
          <label className={labelClassName}>
            {isIndividualPredictionTask
              ? "How would you rate the clarity of the task and its instructions?"
              : "How would you rate the clarity of the game and its instructions?"}
          </label>
          <LikertScale5
            name="gameClarity"
            value={gameClarity}
            onChange={setGameClarity}
            labels={["Very Unclear", "Unclear", "Neutral", "Clear", "Very Clear"]}
          />
        </div>

        {/* Fair Pay */}
        <div>
          <label className={labelClassName}>
            Do you feel the pay was fair?
          </label>
          <LikertScale5
            name="fairPay"
            value={fairPay}
            onChange={setFairPay}
            labels={["Very Unfair", "Unfair", "Neutral", "Fair", "Very Fair"]}
          />
        </div>

        {/* Display Problems */}
        <div>
          <label className={labelClassName}>
            Did you experience any problems with the display?
          </label>
          <div className="mt-2 flex gap-6">
            <Radio
              selected={displayProblems}
              name="displayProblems"
              value="yes"
              label="Yes"
              onChange={(e) => setDisplayProblems(e.target.value)}
            />
            <Radio
              selected={displayProblems}
              name="displayProblems"
              value="no"
              label="No"
              onChange={(e) => setDisplayProblems(e.target.value)}
            />
          </div>
        </div>

        {/* Additional Feedback */}
        <div>
          <label className={labelClassName}>
            Any additional feedback, including problems you encountered?
          </label>
          <textarea
            className={inputClassName}
            id="additionalFeedback"
            name="additionalFeedback"
            rows={4}
            placeholder="Optional - please share any additional thoughts..."
            value={additionalFeedback}
            onChange={(e) => setAdditionalFeedback(e.target.value)}
          />
        </div>
      </div>
    );
  }

  function renderPage3() {
    const completionMessage = isLobbyFailure
      ? "Thank you for your time and patience!"  // Player didn't successfully enter a group
      : "Thank you for participating!";  // Player was part of a matched group

    const descriptionMessage = isLobbyFailure
      ? "Sorry, you did not get to play the game. Please come back next time."
      : "Your responses have been recorded successfully. Here is your completion code for Prolific:";

    const paymentNote = isLobbyFailure
      ? `You will receive the ${compensation.basePaymentAmountText} base payment for your participation time.`
      : "";

    return (
      <div className="space-y-8 mt-6 text-center">
        <div className="max-w-2xl mx-auto">
          <div className="mb-8">
            <h3 className="text-2xl font-bold text-gray-900 mb-4">
              {completionMessage}
            </h3>
            {isLobbyFailure ? (
              <div className="bg-yellow-50 border-2 border-yellow-400 rounded-lg p-6 mb-6">
                <p className="text-lg text-gray-800 font-medium">
                  {descriptionMessage}
                </p>
              </div>
            ) : (
              <p className="text-lg text-gray-700 mb-6">
                {descriptionMessage}
              </p>
            )}
            {(isLobbyTimeout || isLobbyFailure) && paymentNote && (
              <p className="text-md text-blue-700 font-medium mb-4">
                {paymentNote}
              </p>
            )}
          </div>

          {/* Always show completion code. One code for every condition. */}
          <div className="bg-gray-50 border-2 border-dashed border-gray-300 rounded-lg p-8 mb-8">
            <div className="text-3xl font-mono font-bold text-empirica-600 mb-2">
              C1DDWYC8
            </div>
            <p className="text-sm text-gray-600">
              Please copy this code and paste it in Prolific to complete your submission.
            </p>
          </div>

          <div className="text-sm text-gray-500">
            <p>You may now close this window.</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="py-8 max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
      <form
        className="mt-12 space-y-8 divide-y divide-gray-200"
        onSubmit={handleSubmit}
      >
        <div className="space-y-8 divide-y divide-gray-200">
          <div>
            {isLobbyFailure ? renderPage3() :
             currentPage === 1 ? renderPage1() :
             currentPage === 2 ? renderPage2() :
             renderPage3()}

            {!isLobbyFailure && (
              <div className="mt-12 flex justify-end">
                <Button type="submit">
                  {currentPage === 1 ? "Next" : currentPage === 2 ? "Submit Survey" : "Complete & Exit"}
                </Button>
              </div>
            )}
          </div>
        </div>
      </form>
    </div>
  );
}

export function Radio({ selected, name, value, label, onChange }) {
  return (
    <label className="text-sm font-medium text-gray-700">
      <input
        className="mr-2 shadow-sm sm:text-sm"
        type="radio"
        name={name}
        value={value}
        checked={selected === value}
        onChange={onChange}
      />
      {label}
    </label>
  );
}

export function LikertScale7({ name, value, onChange, labels }) {
  const options = [1, 2, 3, 4, 5, 6, 7];

  return (
    <div className="flex justify-between items-end mt-2">
      {options.map((option, index) => (
        <label key={option} className="flex flex-col items-center min-w-0 flex-1">
          <input
            type="radio"
            name={name}
            value={option}
            checked={value === option.toString()}
            onChange={(e) => onChange(e.target.value)}
            className="mb-2"
          />
          <span className="text-xs text-gray-500 text-center leading-tight">
            {labels[index] || option}
          </span>
        </label>
      ))}
    </div>
  );
}

export function LikertScale5({ name, value, onChange, labels }) {
  const options = [1, 2, 3, 4, 5];

  return (
    <div className="flex justify-between items-end mt-2">
      {options.map((option, index) => (
        <label key={option} className="flex flex-col items-center min-w-0 flex-1">
          <input
            type="radio"
            name={name}
            value={option}
            checked={value === option.toString()}
            onChange={(e) => onChange(e.target.value)}
            className="mb-2"
          />
          <span className="text-xs text-gray-500 text-center leading-tight">
            {labels[index] || option}
          </span>
        </label>
      ))}
    </div>
  );
}
